/**
 * AYUNEL「最近見た商品」行为层(sections/ayunel-recently-viewed.liquid 的 <ayunel-recently-viewed>)。
 *
 * 只做三件事:
 * 1. 在商品页记下当前商品(本机 localStorage,按商品编号,最多 12 件,最近看的在前)。
 * 2. 滑到附近时,用站内搜索 id:… OR … 按 Section Rendering API 取本区块(同官方 predictive-search.js
 *    「最近見た商品」与 product-recommendations.js 的取数法),官方 morphSection 的 hydration 模式接进页面。只取一次。
 * 3. 去掉商品链接上站内搜索的统计参数(_pos / _sid / _ss),免得「最近見た商品」的点击被记成站内搜索点击。
 *
 * 为什么不直接用官方 RecentlyViewed(assets/recently-viewed-products.js)的记录:它上限写死 4 件(私有常量),
 * 去掉当前商品后商品页最多只剩 3 件;而把它改大会连带搜索框的「最近見た商品」—— 那段 Liquid 按编号出卡时
 * 没有数量上限,2026-09-16 独立审查实测改 12 后搜索框出 12 张、其中 2 张是空链接坏卡。
 * ⇒ 官方数量上限不改,本模块另存一份;第一次运行时把官方那份(最多 4 件)接过来,老客人的记录不从零开始;
 *   客人在搜索框点「クリア」时,RecentlyViewed.clearProducts 同时移除两份记录;下一商品页重新记当前商品。
 */
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { RecentlyViewed } from '@theme/recently-viewed-products';

const STORAGE_KEY = 'ayunel_recently_viewed_v1';
const MAX_STORED = 12;
/** 官方 RecentlyViewed 的上限(私有常量 #MAX_PRODUCTS) */
const UPSTREAM_MAX = 4;
const SEARCH_TRACKING_PARAMS = ['_pos', '_sid', '_ss'];

/**
 * 只留纯数字编号、去重、保序。记录里混进一个坏值(手改存储 / 插件 / 以后的代码改动),
 * 拼出的 `id:abc OR id:123` 在站内搜索里整条查不到 —— 整块消失,而不是只少一张(2026-09-16 独立审查 DEV 实测)。
 * @param {unknown} value
 * @returns {string[] | null} 不是数组时返回 null
 */
function normalizeIds(value) {
  if (!Array.isArray(value)) return null;
  return [...new Set(value.map((id) => String(id).trim()).filter((id) => /^\d+$/.test(id)))];
}

/** @returns {string[] | null} 本模块的记录;从没存过(或读不了)时返回 null */
function readStored() {
  try {
    return normalizeIds(JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null'));
  } catch {
    return null;
  }
}

/** @returns {string[] | null} 官方那份;读不了时返回 null(读不了 ≠ 被清空) */
function readUpstream() {
  try {
    return normalizeIds(RecentlyViewed.getProducts());
  } catch {
    return null;
  }
}

/** @returns {string[]} 最近看的在前;第一次运行时接官方那份 */
function getViewedIds() {
  return readStored() ?? readUpstream() ?? [];
}

/** @param {string} productId */
function recordView(productId) {
  let ids = getViewedIds();
  const upstream = readUpstream();
  // 兼容旧版已清过官方记录的页面:除了变短,「官方只剩当前商品、旧记录不含当前商品」也表示已经清过。
  // 只比长度会漏掉「旧记录只有 A → 清除 → 官方先记 B」两边长度都为 1 的情形。
  if (upstream && readStored() && (
    upstream.length < Math.min(ids.length, UPSTREAM_MAX) ||
    (upstream.length === 1 && upstream[0] === productId && !ids.includes(productId))
  )) ids = [];
  ids = [productId, ...ids.filter((id) => id !== productId)].slice(0, MAX_STORED);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // 写不进去(存储满 / 被禁用):这次不记当前商品;已有记录照常显示,读也读不了时区块保持空壳
  }
}

class AyunelRecentlyViewed extends HTMLElement {
  /**
   * 只取一次。取数后 morph 会把服务端返回的属性覆盖到本元素上,而返回是在搜索页上渲染的(没有 product),
   * data-product-id 会变成空 —— 重挂后再取就会把正在看的商品也取回来(独立审查实测)。
   */
  #loaded = false;

  #observer = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#load();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  connectedCallback() {
    const { productId } = this.dataset;
    if (productId) recordView(productId);

    // 编辑器里由 Liquid 出占位卡;官方也注明编辑器里的区块 id 取不了 Section Rendering API
    if (window.Shopify?.designMode || this.#loaded) return;
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer.disconnect();
  }

  async #load() {
    const { sectionId, productId, searchUrl } = this.dataset;
    const ids = getViewedIds().filter((id) => id !== productId);
    if (!sectionId || !searchUrl || ids.length === 0) return;

    const url = new URL(searchUrl, window.location.origin);
    url.searchParams.set('q', ids.map((id) => `id:${id}`).join(' OR '));
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[prefix]', 'none');
    url.searchParams.set('options[unavailable_products]', 'show');

    this.#loaded = true;
    try {
      const html = await sectionRenderer.getSectionHTML(sectionId, false, url);
      await morphSection(sectionId, html, { mode: 'hydration', injectStylesheet: true });
      this.#stripSearchTracking();
    } catch (error) {
      console.warn('Recently viewed products could not be loaded:', error);
    }
  }

  #stripSearchTracking() {
    for (const link of this.querySelectorAll('a[href]')) {
      const url = new URL(link.href, window.location.origin);
      if (!SEARCH_TRACKING_PARAMS.some((param) => url.searchParams.has(param))) continue;
      for (const param of SEARCH_TRACKING_PARAMS) url.searchParams.delete(param);
      link.href = `${url.pathname}${url.search}${url.hash}`;
    }
  }
}

if (!customElements.get('ayunel-recently-viewed')) {
  customElements.define('ayunel-recently-viewed', AyunelRecentlyViewed);
}
